import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { URL_EXTENSION } from '@utils/constants/app-constants';
import type { Folder, FoldersResponse } from '@utils/types/folders/folder';
import { fetchFolder } from './use-get-folder';

// The list API returns direct children only. Read each level, including every
// page, without inheriting the workspace's search, dates or pagination.
export async function fetchFolderTree(): Promise<Folder[]> {
  const pageSize = 1000;
  const readChildren = async (id: number | null) => {
    const children: Folder[] = [];
    let offset = 0;
    let total = Infinity;
    while (offset < total) {
      const response =
        id === null
          ? (
              await Axios.get<FoldersResponse>(
                `${URL_EXTENSION}/folders?type=folders&sort=name&direction=asc&offset=${offset}&limit=${pageSize}`
              )
            ).data
          : await fetchFolder(String(id), {
              type: 'folders',
              sort: 'name',
              direction: 'asc',
              offset,
              limit: pageSize
            });
      // An aborted/truncated response can land here with no `result` (status 0
      // / empty body / mid-navigation unmount). Treat it as "no more children"
      // rather than throwing on `.result.child_folders`.
      const page =
        response?.result?.child_folders ?? response?.result?.folders ?? [];
      children.push(...page);
      total = response?.total_results ?? 0;
      if (page.length === 0) break;
      offset += page.length;
    }
    return children;
  };

  const roots = await readChildren(null);
  const queue = [...roots];
  const visited = new Set<number>();
  while (queue.length) {
    // Bound concurrency for large accounts.
    const batch = queue.splice(0, 6).filter(folder => {
      if (visited.has(folder.id)) return false;
      visited.add(folder.id);
      return true;
    });
    await Promise.all(
      batch.map(async folder => {
        folder.child_folders = await readChildren(folder.id);
        queue.push(...folder.child_folders);
      })
    );
  }
  return roots;
}

export function useFolderTree(enabled = true) {
  return useQuery(['folders', 'tree'], fetchFolderTree, {
    enabled,
    staleTime: 30_000
  });
}
